import { BaseService, Service } from "/@/cool";
import { DEFAULT_EQUIPMENT_MASTER_BASE_URL } from "../takePhotoWorkBenchV3/constants/master-server";
import { DEFAULT_COVER_DEVICE_ID } from "../takePhotoWorkBenchV3/constants/master-server";
import type {
	MasterCollectProgress,
	MasterCoverUrl,
	MasterLocalPhotoDevices,
	MasterPhotoOssUploadMeta,
	MasterPhotoUrlList,
	MasterStartTakePhotoResult,
	MasterTakePhotoFlow,
	MasterUploadPhotosByUrlItem,
	MasterUploadPhotosByUrlsStatus
} from "../takePhotoWorkBenchV3/types/master-flow";

/**
 * 影棚主控 HTTP API（suwa3d_equipment_server）
 * 直连局域网主控，不走云端 shop.api
 */
@Service("equipmentMaster")
class EquipmentMaster extends BaseService {
	/**
	 * 解析主控 Base URL
	 * @param baseUrl - 可选覆盖，默认 https://192.168.88.2（HTTPS :443）
	 */
	resolveBaseUrl(baseUrl?: string): string {
		const url = (baseUrl || DEFAULT_EQUIPMENT_MASTER_BASE_URL).replace(/\/$/, "");
		return url;
	}

	/**
	 * 查询拍照流程状态
	 * GET /api/getTakePhotoFlow
	 */
	getTakePhotoFlow(baseUrl?: string): Promise<MasterTakePhotoFlow> {
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/getTakePhotoFlow`,
			method: "GET",
			timeout: 3000
		});
	}

	/**
	 * 准备拍照（触发相机同步）
	 * GET /api/readyTakePhoto
	 */
	readyTakePhoto(baseUrl?: string): Promise<null> {
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/readyTakePhoto`,
			method: "GET",
			timeout: 15000
		});
	}

	/**
	 * Shop 端开始拍照
	 * GET /api/startTakePhotoByShop?pid=
	 */
	startTakePhotoByShop(pid: number, baseUrl?: string): Promise<MasterStartTakePhotoResult> {
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/startTakePhotoByShop`,
			method: "GET",
			params: { pid },
			timeout: 15000
		});
	}

	/**
	 * 采集进度（拍照后 FTP 落盘）
	 * GET /api/collectProgress?photo_id=
	 */
	collectProgress(photoId: number, baseUrl?: string): Promise<MasterCollectProgress> {
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/collectProgress`,
			method: "GET",
			params: { photo_id: photoId },
			timeout: 3000
		});
	}

	/**
	 * 列表封面图（待审核缩略图）
	 * GET /api/coverUrl?photo_id=&cover_device_id=
	 * @param photoId - 拍照 photo_id / 卡片 pid
	 * @param coverDeviceId - 优先机位（影棚类型封面相机；缺省兜底 123）
	 * @param baseUrl - 主控地址
	 */
	coverUrl(
		photoId: number,
		coverDeviceId: number = DEFAULT_COVER_DEVICE_ID,
		baseUrl?: string
	): Promise<MasterCoverUrl> {
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/coverUrl`,
			method: "GET",
			params: {
				photo_id: photoId,
				cover_device_id: coverDeviceId || DEFAULT_COVER_DEVICE_ID
			},
			timeout: 5000
		});
	}

	/**
	 * 按 photo_id 获取各机位照片链接
	 * GET /api/photoUrlList?photo_id=&device_ids=&file_type=&skip_heal=
	 * @param photoId - 拍照 photo_id / 卡片 pid
	 * @param options - deviceIds；fileType 1 预览 2 高清；skipHeal 默认 true 跳过补采（需补采时传 false）
	 * @param baseUrl - 主控地址
	 */
	photoUrlList(
		photoId: number,
		options?: { deviceIds?: string | number[]; fileType?: number; skipHeal?: boolean },
		baseUrl?: string
	): Promise<MasterPhotoUrlList> {
		const deviceIds = Array.isArray(options?.deviceIds)
			? options!.deviceIds.join(",")
			: options?.deviceIds;
		const skipHeal = options?.skipHeal ?? true;
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/photoUrlList`,
			method: "GET",
			params: {
				photo_id: photoId,
				device_ids: deviceIds || undefined,
				file_type: options?.fileType ?? 2,
				skip_heal: skipHeal ? 1 : undefined
			},
			// skip_heal 默认开启；显式开启 heal 时主控最长约 15s，留足余量
			timeout: 45000
		});
	}

	/**
	 * 列出本地已落盘高清机位（扫磁盘，不限注册表）
	 * GET /api/localPhotoDevices?photo_id=
	 */
	localPhotoDevices(photoId: number, baseUrl?: string): Promise<MasterLocalPhotoDevices> {
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/localPhotoDevices`,
			method: "GET",
			params: { photo_id: photoId },
			timeout: 10000
		});
	}

	/**
	 * 删除主控本地照片会话
	 * DELETE /api/photoSessions?photo_ids=
	 */
	deletePhotoSessions(
		photoIds: number | number[],
		baseUrl?: string
	): Promise<{
		deleted?: number[];
		failed?: { photo_id: number; error: string }[];
	}> {
		const ids = Array.isArray(photoIds) ? photoIds.join(",") : String(photoIds);
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/photoSessions`,
			method: "DELETE",
			params: { photo_ids: ids },
			timeout: 30000
		});
	}

	/**
	 * 待离线同步会话列表（首次 + 续传 ing/failed）
	 * GET /api/offlineSessions
	 */
	offlineSessions(
		baseUrl?: string,
		limit?: number
	): Promise<{
		sessions: Array<{
			local_pid: number;
			cloud_pid?: number;
			customer_id?: number;
			upload_status?: string;
			resume?: boolean;
			customer_name: string;
			phone: string;
			address?: string;
			photo_count: number;
			sort_time?: string;
			created_at?: string;
			cover_url?: string;
		}>;
		total: number;
	}> {
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/offlineSessions`,
			method: "GET",
			params: limit ? { limit } : undefined,
			timeout: 10000
		});
	}

	/**
	 * 同步开始时绑定 cloud_pid（默认 upload_status=ing）
	 * POST /api/offlineSessions/bind
	 */
	bindOfflineSession(
		data: {
			local_pid: number;
			cloud_pid: number;
			customer_id?: number;
			upload_status?: string;
		},
		baseUrl?: string
	): Promise<{
		local_pid: number;
		cloud_pid: number;
		customer_id?: number;
		upload_status?: string;
		customer_name?: string;
		phone?: string;
		address?: string;
	}> {
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/offlineSessions/bind`,
			method: "POST",
			data,
			timeout: 10000
		});
	}

	/**
	 * 更新离线同步 upload_status
	 * POST /api/offlineSessions/mark
	 */
	markOfflineSession(
		data: { local_pid: number; upload_status: "ing" | "failed" | "done" },
		baseUrl?: string
	): Promise<{
		local_pid: number;
		cloud_pid: number;
		upload_status?: string;
	}> {
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/offlineSessions/mark`,
			method: "POST",
			data,
			timeout: 10000
		});
	}

	/**
	 * 旁路：下发预签名 URL，由主控本地取图 PUT 到 OSS
	 * POST /api/uploadPhotosByUrls
	 */
	uploadPhotosByUrls(
		data: { pid: number; items: MasterUploadPhotosByUrlItem[] },
		baseUrl?: string
	): Promise<MasterUploadPhotosByUrlsStatus> {
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/uploadPhotosByUrls`,
			method: "POST",
			data,
			timeout: 30000
		});
	}

	/**
	 * 旁路：查询上传进度
	 * GET /api/uploadPhotosByUrlsStatus?pid=
	 */
	uploadPhotosByUrlsStatus(
		pid: number,
		baseUrl?: string
	): Promise<MasterUploadPhotosByUrlsStatus> {
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/uploadPhotosByUrlsStatus`,
			method: "GET",
			params: { pid },
			timeout: 5000
		});
	}

	/**
	 * 旁路：终止上传（已成功 meta 保留，可再次下发）
	 * POST /api/cancelUploadPhotosByUrls
	 */
	cancelUploadPhotosByUrls(
		pid: number,
		baseUrl?: string
	): Promise<MasterUploadPhotosByUrlsStatus> {
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/cancelUploadPhotosByUrls`,
			method: "POST",
			data: { pid },
			timeout: 30000
		});
	}

	/**
	 * 旁路：查询 meta 上传统计（备用；续传以云端 ossExists 为准）
	 * GET /api/photoOssUploadMeta?pid=
	 */
	photoOssUploadMeta(pid: number, baseUrl?: string): Promise<MasterPhotoOssUploadMeta> {
		return this.request({
			url: `${this.resolveBaseUrl(baseUrl)}/api/photoOssUploadMeta`,
			method: "GET",
			params: { pid },
			timeout: 5000
		});
	}
}

export default EquipmentMaster;
